import debounce from 'lodash/debounce';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router';

import { BackstageContentSections } from '../../../constants/ContentSections';
import QA from '../../../constants/QATags';
import { AppContext } from '../../../lib/Context';
import { getBackstageLocation } from '../../../lib/RouteUtils';
import {
  getDescriptionBackstageAnchorStringIndex,
  parseDescriptionBackstageStringToReactComponent,
  truncateReactComponentArray,
} from '../../../lib/StringUtils';
import { createBEM } from '../../../lib/utils/css';
import { withVisibilityLogger } from '../../VisibilityLogger/VisibilityLogger';

import { descriptionBackstageMessage } from './constants';

import './DescriptionBackstage.scss';

class DescriptionBackstage extends React.Component {
  static propTypes = {
    text: PropTypes.string,
    isExpanded: PropTypes.bool,
    isSmall: PropTypes.bool,
    isLarge: PropTypes.bool,
    isMobileDevice: PropTypes.bool,
    linkUrl: PropTypes.string,
    linkText: PropTypes.object,
    disableHover: PropTypes.bool,
    onDescriptionLinkClick: PropTypes.func,
    shortTextWidthRatio: PropTypes.number,
    hasBackgroundImg: PropTypes.bool,
    shouldShowSeeMore: PropTypes.bool,
  };

  static defaultProps = {
    shouldShowSeeMore: true,
  };

  constructor(props) {
    super(props);
    this.updateTextLength = debounce(this.updateTextLength, 100);
  }

  state = {
    shortTextLength: 200,
    mediumTextLength: 500,
    largeTextLength: 1000,
  };

  componentDidMount() {
    window.addEventListener('resize', this.updateTextLength);
    this.updateTextLength();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateTextLength);
  }

  static contextType = AppContext;
  refContentText = null;

  setRefContentText = refContentText => {
    this.refContentText = refContentText;
  };

  getShortTextWidthRatio() {
    return this.props.shortTextWidthRatio || 3.4;
  }

  useShortTextLength = () => {
    return !this.props.isLarge;
  };

  useMediumTextLength = () => {
    return this.props.isLarge && this.props.isMobileDevice;
  };

  useLargeTextLength = () => {
    return this.props.isLarge && !this.props.isMobileDevice;
  };

  updateTextLength = () => {
    if (this.refContentText) {
      if (this.useLargeTextLength()) {
        this.setState({
          largeTextLength: Math.floor(this.refContentText.clientWidth),
        });
      } else if (this.useMediumTextLength()) {
        this.setState({
          mediumTextLength: Math.floor(this.refContentText.clientWidth),
        });
      } else if (this.useShortTextLength()) {
        this.setState({
          shortTextLength: Math.floor(
            this.refContentText.clientWidth / this.getShortTextWidthRatio(),
          ),
        });
      }
    }
  };

  checkIsOverflowing = el => {
    if (!el || this.props.isExpanded) {
      return;
    }
  };

  getTextLength = () => {
    let textLength = 0;

    if (this.useLargeTextLength()) {
      textLength = this.state.largeTextLength;
    }

    if (this.useMediumTextLength()) {
      textLength = this.state.mediumTextLength;
    }

    if (this.useShortTextLength()) {
      textLength = this.state.shortTextLength;
    }

    return textLength;
  };

  getLinkText = () => {
    return this.props.linkText ? this.props.linkText : descriptionBackstageMessage.seeAll;
  };

  shouldShowSeeMore = () => {
    const fullText = this.props.text;
    const textFloorLength = this.getTextLength();

    if (!fullText) {
      return false;
    }

    const fullTextLength = fullText.length;
    const difference = Math.abs(fullTextLength - textFloorLength);
    // This isn't a full-proof solution, but by checking the text length differences when the lengths are close,
    // we could somewhat ensure that the read more text will appear. The 50 value seemed to be the most consistent number
    // but it's completely arbitrary
    return (
      fullTextLength > textFloorLength || (fullTextLength < textFloorLength && difference < 50)
    );
  };

  onDescriptionLinkClick = e => {
    this.context.integration.action.clicked({
      contentSection: BackstageContentSections.DESCRIPTION,
      destinationUrl: e.currentTarget.pathname,
    });
  };

  renderText = () => {
    let text =
      this.props.text &&
      parseDescriptionBackstageStringToReactComponent(
        this.props.text,
        getBackstageLocation,
        this.onDescriptionLinkClick,
      );
    if (!this.props.isExpanded) {
      text = truncateReactComponentArray(text, this.getTextLength());
      if (text && text.length > 0 && this.shouldShowSeeMore()) {
        const seeMoreClassNames = createBEM('DescriptionBackstage__content__seeMore', {
          hasBackgroundImg: this.props.hasBackgroundImg,
        });

        text.push(
          <React.Fragment>
            ...
            {this.props.shouldShowSeeMore && (
              <span className={seeMoreClassNames}>
                <Link
                  to={this.props.linkUrl}
                  key={getDescriptionBackstageAnchorStringIndex()}
                  data-qa={QA.DBL}
                  onClick={this.onDescriptionLinkClick}
                >
                  <FormattedMessage {...this.getLinkText()} />
                </Link>
              </span>
            )}
          </React.Fragment>,
        );
      }
      return text.map((element, i) => <span key={i}>{element}</span>);
    }
    return text;
  };

  render() {
    const descriptionClasses = createBEM('DescriptionBackstage', {
      disableHover: this.props.disableHover,
      small: this.props.isSmall,
      withBackgroundImg: this.props.hasBackgroundImg,
    });
    const contentClasses = createBEM('DescriptionBackstage__content', {
      expanded: this.props.isExpanded,
      large: this.props.isLarge,
      mobile: this.props.isMobileDevice,
    });
    return (
      <div className={descriptionClasses}>
        <div className={contentClasses} ref={this.checkIsOverflowing} data-qa={QA.DBT}>
          <div className='DescriptionBackstage__content__text' ref={this.setRefContentText}>
            {this.renderText()}
          </div>
        </div>
      </div>
    );
  }
}

export const DescriptionBackstageWithLogger = withVisibilityLogger(
  { contentSection: BackstageContentSections.DESCRIPTION },
  { useMemo: true },
)(DescriptionBackstage);

export default DescriptionBackstage;
